import { Helmet } from 'react-helmet';
import Services from './../../Components/Services/Services';
import CallToAction from './../../Components/CallToAction/CallToAction';
import { Box } from '@mui/material';
import VaPdf from './../../Components/VaPdf/VaPdf';
import useStyles from './Home.Styles';

function Home() {
    const classes = useStyles();
    return (
        <section>
            <Helmet>
                <title>Certified Well Water Testing & Flow Rate (GPM) | North Idaho & Eastern WA</title>
                <meta
                    name="description"
                    content="VA/FHA/USDA-compliant well water testing, flow rate (GPM), bacteria, nitrates. Fast results for real estate in Coeur d’Alene, Spokane, Sandpoint & beyond."
                />
                <meta
                    name="keywords"
                    content="well water testing, certified water testing, flow rate testing, system inspections, North Idaho, Eastern Washington, VA FHA USDA HUD loan testing, real estate water testing"
                />
                <link rel="canonical" href="https://chinookwater.com" />

                {/* Open Graph */}
                <meta property="og:title" content="Certified Well Water Testing & Flow Rate (GPM) | North Idaho & Eastern WA" />
                <meta property="og:description" content="VA/FHA/USDA-compliant well water testing & flow rate inspections. Fast, trusted results for real estate." />
                <meta property="og:url" content="https://chinookwater.com" />
                <meta property="og:image" content="https://chinookwater.com/chinookLogo.webp" />
                <meta property="og:image:alt" content="Chinook Water Testing company logo" />

                {/* Twitter (parity with OG; no image changes) */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Certified Well Water Testing & Flow Rate (GPM) | North Idaho & Eastern WA" />
                <meta name="twitter:description" content="VA/FHA/USDA-compliant well water testing & flow rate inspections. Fast, trusted results for real estate." />
                <meta name="twitter:image" content="https://chinookwater.com/chinookLogo.webp" />

                {/* BREADCRUMB JSON-LD */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        itemListElement: [
                            {
                                "@type": "ListItem",
                                position: 1,
                                name: "Home",
                                item: "https://chinookwater.com"
                            }
                        ]
                    })}
                </script>

                {/* FAQPAGE JSON-LD (kept; content is visible via Services component) */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        mainEntity: [
                            {
                                "@type": "Question",
                                name: "What types of well water testing do you offer?",
                                acceptedAnswer: {
                                    "@type": "Answer",
                                    text: "We offer Bacterial and Potability Testing, Heavy Metals Testing (upon request), and VA, FHA, USDA & HUD Loan Compliance Testing using a state-certified lab."
                                }
                            },
                            {
                                "@type": "Question",
                                name: "Do you perform flow rate (GPM) testing?",
                                acceptedAnswer: {
                                    "@type": "Answer",
                                    text: "Yes. We provide HUD 4000.1 Productivity Verification, VA/FHA/USDA/HUD loan-compliant flow rate testing, system performance analysis, and well production testing for real estate sales."
                                }
                            },
                            {
                                "@type": "Question",
                                name: "Do you inspect water and wastewater systems?",
                                acceptedAnswer: {
                                    "@type": "Answer",
                                    text: "Yes. We perform operational checks, performance validation, compliance consultation, and full VA/FHA/USDA/HUD loan-compliant system inspections."
                                }
                            }
                        ]
                    })}
                </script>

                {/* NEW: WebPage JSON-LD (links home to your site/business entities) */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "WebPage",
                        "@id": "https://chinookwater.com/#home",
                        url: "https://chinookwater.com",
                        name: "Certified Well Water Testing & Flow Rate (GPM) | North Idaho & Eastern WA",
                        description:
                            "VA/FHA/USDA-compliant well water testing, flow rate (GPM), bacteria, nitrates. Fast results for real estate in Coeur d’Alene, Spokane, Sandpoint & beyond.",
                        isPartOf: { "@id": "https://chinookwater.com/#website" },
                        about: { "@id": "https://chinookwater.com/#business" }
                    })}
                </script>

                {/* NEW: Services ItemList JSON-LD (3 core services with provider linkage) */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "ItemList",
                        name: "Services",
                        itemListElement: [
                            {
                                "@type": "ListItem",
                                position: 1,
                                item: {
                                    "@type": "Service",
                                    "@id": "https://chinookwater.com/#service-water-testing",
                                    name: "Well Water Sampling & Testing",
                                    description:
                                        "Bacterial & potability testing, heavy metals on request, VA/FHA/USDA/HUD loan compliance via state-certified lab.",
                                    provider: { "@id": "https://chinookwater.com/#business" },
                                    areaServed: "North Idaho & Eastern Washington"
                                }
                            },
                            {
                                "@type": "ListItem",
                                position: 2,
                                item: {
                                    "@type": "Service",
                                    "@id": "https://chinookwater.com/#service-flow-rate",
                                    name: "Flow Rate Testing (GPM)",
                                    description:
                                        "Flow verification and well production analysis for real estate transactions and loan underwriting.",
                                    provider: { "@id": "https://chinookwater.com/#business" },
                                    areaServed: "North Idaho & Eastern Washington"
                                }
                            },
                            {
                                "@type": "ListItem",
                                position: 3,
                                item: {
                                    "@type": "Service",
                                    "@id": "https://chinookwater.com/#service-system-inspection",
                                    name: "System Inspections",
                                    description:
                                        "Operational checks, compliance validation, and detailed reports for private well and wastewater systems.",
                                    provider: { "@id": "https://chinookwater.com/#business" },
                                    areaServed: "North Idaho & Eastern Washington"
                                }
                            }
                        ]
                    })}
                </script>
            </Helmet>

            <header className="sr-only">
                Certified Well Water Testing, Flow Rate Testing & System Inspections in North Idaho & Eastern WA
            </header>

            <main>
                <Box className={classes.titleBox}>
                    <h1 className={classes.title}>
                        Certified Well Water Testing, Flow Rate Testing (GPM) & System Inspections in North Idaho and Eastern Washington.
                    </h1>
                    <h2 className={classes.subTitle}>
                        Serving Homeowners, Real Estate Agents, Loan Officers & Inspectors for VA, FHA, USDA & HUD Loan Water Testing.
                    </h2>
                    <Box className={classes.pdfBox}>
                        <VaPdf />
                    </Box>
                </Box>

                <Services />

                {/* Call to Action */}
                <CallToAction />
            </main>
        </section>
    );
}

export default Home;
